// components/Student360Report.jsx
import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import StudentMiscReceipts from '../ledger/StudentMiscReceipts';

const Student360Report = ({
  show,
  onHide,
  studentData,
  userInfo,
  title = "Student 360° Report"
}) => {
  
  const [isDownloading, setIsDownloading] = useState(false);

  // ========== PDF GENERATION FUNCTION INSIDE COMPONENT ==========
  const generatePDF = async () => {
    if (!studentData) return;
    
    setIsDownloading(true);
    try {
      const element = document.getElementById('pdf-preview-content');
      console.log("Generating PDF for element:", element);
      if (!element) throw new Error("Element not found");

      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: "#ffffff",
        logging: false,
        useCORS: true,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = position - pageHeight;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Add page numbers
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Page ${i} of ${totalPages}`, 105, 287, { align: "center" });
      }

      const fileName = `Student_360_${studentData?.student_info?.firstname || 'Student'}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      onHide(); // Close modal after download
    } catch (error) {
      console.error("PDF Generation Error:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  // Table Field Component
  const TableField = ({ label, value }) => (
    <div style={{ marginBottom: "20px", }}>
      <div style={{ fontSize: "11px", color: "#1e2a5a", textTransform: "uppercase", marginBottom: "5px" }}>
        {label}
      </div>
      <div style={{ borderBottom: "1px solid #ccc", minHeight: "22px", fontSize: "14px", paddingBottom: "4px", color: "#222" }}>
        {value || " "}
      </div>
    </div>
  );

  // Report Header
  const ReportHeader = () => (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "25px",
      paddingBottom: "15px",
      borderBottom: "3px solid #007bff",
      paddingLeft: "20px",
      marginLeft: "15px",
      marginRight: "15px",
    }}>
      <div style={{ width: "100px", height: "100px", flexShrink: 0 }}>
        <img
          src={`/${userInfo?.tenantcode}-sidebar-logo.png`}
          alt="Company Logo"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          onError={(e) => {
            e.target.src = "/uploads/dwps_ajmer/image_2024102442318.png";
          }}
        />
      </div>
      <div style={{ flex: 1, textAlign: "center" }}>
        <h2 style={{ color: "#007bff", margin: 0, }}>
          {userInfo?.companyname || title}
        </h2>
        <p className="mt-2">
          {userInfo?.companystreet || ""} {userInfo?.companystate || ""} {userInfo?.companypincode || ""}
        </p>
        <small style={{ fontSize: "10px", color: "#666" }}>
          Generated on: {new Date().toLocaleString()}
        </small>
      </div>
      <div style={{ width: "100px" }}></div>
    </div>
  );

  // Student Info Section
  const StudentInfoSection = ({ data }) => (
    
    <div style={{ marginBottom: "10px", paddingLeft: "10px",
    paddingRight: "10px", }} >
      <h5 style={{ background: "#6c757d", color: "white", padding: "8px", borderRadius: "4px", marginBottom: "15px" }}>
        Student Information
      </h5>
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        {/* Left Column */}
        <div style={{ width: "33.33%", padding: "10px", boxSizing: "border-box" }}>
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <div style={{ width: "120px", height: "120px", borderRadius: "50%", overflow: "hidden", border: "3px solid #0077b6", margin: "auto" }}>
              <img
                key={`${data?.file_id}-${userInfo?.tenantcode || 'default'}`}
                src={data?.file_id ? (data.file_parent_id ? `/uploads/${userInfo?.tenantcode}/${data.file_parent_id}/${data.file_id}.${data.file_type}?v=${Date.now()}` : `/uploads/${userInfo?.tenantcode}/${data.file_id}.${data.file_type}?v=${Date.now()}`) : '/uploads/dwps_ajmer/image_2024102442318.png'}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                alt="Student"
                onError={(e) => {
                  const step = parseInt(e.target.dataset.step || '0', 10);
                  e.target.dataset.step = step + 1;
                  if (step === 0 && data?.file_parent_id) {
                    e.target.src = `/uploads/${data.file_parent_id}/${data.file_id}.${data.file_type}?v=${Date.now()}`;
                  } else if (step === 1 && data?.file_id) {
                    e.target.src = `/uploads/${userInfo?.tenantcode}/${data.file_id}.${data.file_type}?v=${Date.now()}`;
                  } else if (step === 2 && data?.file_id) {
                    e.target.src = `/uploads/${data.file_id}.${data.file_type}?v=${Date.now()}`;
                  } else {
                    e.target.src = '/uploads/dwps_ajmer/image_2024102442318.png';
                  }
                }}
              />
            </div>
          </div>
          <TableField label="AADHAR NUMBER" value={data?.adharnumber || " "} />
          <TableField label="SESSION" value={data?.session_year || " "} />
          <TableField label="RELIGION" value={data?.religion || " "} />
          <TableField label="Fees Type" value={data?.fee_master_type || " "} />
          <TableField label="CATEGORY" value={data?.category || " "} />
        </div>

        {/* Middle Column */}
        <div style={{ width: "33.33%", padding: "20px", boxSizing: "border-box" }}>
          <TableField label="SERIAL NO" value={data?.srno || " "} />
          <TableField label="PHONE" value={data?.phone || " "} />
          <TableField label="TOTAL DUES" value={data?.total_dues || " "} />
          <TableField label="Form Number" value={data?.formno || " "} />
          <TableField label="Registration Number" value={data?.registration_no || " "} />
          <TableField label="CLASS NAME" value={data?.class_name || " "} />
          <TableField label="SECTION NAME" value={data?.section_name || " "} />
        </div>

        {/* Right Column */}
        <div style={{ width: "33.33%", padding: "20px", boxSizing: "border-box" }}>
          <TableField label="STUDENT NAME" value={`${data?.firstname || ""} ${data?.lastname || ""}`} />
          <TableField label="DATE OF BIRTH" value={data?.dateofbirth || " "} />
          <TableField label="EMAIL" value={data?.email || " "} />
          <TableField label="STATUS" value={data?.status || " "} />
          <TableField label="TOTAL FEE" value={data?.total_fees || " "} />
          <TableField label="GENDER" value={data?.gender || " "} />
        </div>
      </div>
    </div>
  );

  // Parent Info Section
  const ParentInfoSection = ({ parentInfo }) => {
    if (!parentInfo) return null;
    
    const ParentRow = ({ title, data }) => (
      <div style={{ display: "flex", flexWrap: "wrap", marginBottom: "5px"}}>
        <div style={{ width: "100%", marginBottom: "5px", marginLeft: "5px" }}>
          <div style={{ fontSize: "16px", display: "inline-block" }}>{title } :</div>
        </div>
        <div style={{ width: "25%", padding: "0 10px", boxSizing: "border-box" }}>
          <TableField label={`${title.split(' ')[0]} NAME`} value={`${data?.firstname || ""} ${data?.lastname || ""}`} />
        </div>
        <div style={{ width: "25%", padding: "0 10px", boxSizing: "border-box" }}>
          <TableField label={`${title.split(' ')[0]} PHONE`} value={data?.phone || " "} />
        </div>
        <div style={{ width: "25%", padding: "0 10px", boxSizing: "border-box" }}>
          <TableField label={`${title.split(' ')[0]} EMAIL`} value={data?.email || " "} />
        </div>
        <div style={{ width: "25%", padding: "0 10px", boxSizing: "border-box" }}>
          <TableField label={`${title.split(' ')[0]} OCCUPATION`} value={data?.profession || " "} />
        </div>
      </div>
    );

    return (
      <div style={{ marginBottom: "30px",paddingLeft: "10px",
       paddingRight: "10px", }}>
        <h5 style={{ background: "#6c757d", color: "white", padding: "8px", borderRadius: "4px", margin: "15px 0" }}>
          Parent Information
        </h5>
        <ParentRow title="Father Details" data={parentInfo?.Parent_Father || {}} />
        <ParentRow title="Mother Details" data={parentInfo?.Parent_Mother || {}} />
        <ParentRow title="Guardian Details" data={parentInfo?.Parent_Guardian || {}} />
      </div>
    );
  };

  // Address Section
  const AddressSection = ({ studentInfo }) => (
    <div style={{ marginTop: "150px",paddingLeft: "10px",paddingRight: "10px"}}>
      <h5 style={{ background: "#6c757d", color: "white", padding: "8px", borderRadius: "4px" }}>
        Address Information
      </h5>
      
      {/* Current Address */}
      <div style={{ display: "flex", flexWrap: "wrap", marginBottom: "20px" }}>
        <div style={{ width: "100%", marginBottom: "15px" }}>
          <div style={{ fontSize: "16px", display: "inline-block", marginTop: "20px", paddingLeft: "5px" }}>Current Address :</div>
        </div>
        <div style={{ width: "20%", padding: "0 10px", boxSizing: "border-box" }}>
          <TableField label="STREET" value={studentInfo?.street || " "} />
        </div>
        <div style={{ width: "20%", padding: "0 10px", boxSizing: "border-box" }}>
          <TableField label="CITY" value={studentInfo?.city || " "} />
        </div>
        <div style={{ width: "20%", padding: "0 10px", boxSizing: "border-box" }}>
          <TableField label="STATE" value={studentInfo?.state || " "} />
        </div>
        <div style={{ width: "20%", padding: "0 10px", boxSizing: "border-box" }}>
          <TableField label="PINCODE" value={studentInfo?.pincode || " "} />
        </div>
        <div style={{ width: "20%", padding: "0 10px", boxSizing: "border-box" }}>
          <TableField label="COUNTRY" value={studentInfo?.country || " "} />
        </div>
      </div>

      {/* Permanent Address */}
      <div style={{ display: "flex", flexWrap: "wrap", marginBottom: "20px" }}>
        <div style={{ width: "100%", marginBottom: "15px" }}>
          <div style={{ fontSize: "16px", display: "inline-block",paddingLeft: "5px"}}>Permanent Address :</div>
        </div>
        <div style={{ width: "20%", padding: "0 10px", boxSizing: "border-box" }}>
          <TableField label="PERMANENT STREET" value={studentInfo?.permanentstreet || " " } />
        </div>
        <div style={{ width: "20%", padding: "0 10px", boxSizing: "border-box" }}>
          <TableField label="PERMANENT CITY" value={studentInfo?.permanentcity || " "} />
        </div>
        <div style={{ width: "20%", padding: "0 10px", boxSizing: "border-box" }}>
          <TableField label="PERMANENT STATE" value={studentInfo?.permanentstate || " "} />
        </div>
        <div style={{ width: "20%", padding: "0 10px", boxSizing: "border-box" }}>
          <TableField label="PERMANENT PINCODE" value={studentInfo?.permanentpostalcode || " "} />
        </div>
        <div style={{ width: "20%", padding: "0 10px", boxSizing: "border-box" }}>
          <TableField label="PERMANENT COUNTRY" value={studentInfo?.permanentcountry || " " } />
        </div>
      </div>
    </div>
  );

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {studentData?.student_info?.firstname || ''} {studentData?.student_info?.lastname || ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto", background: "white" }}>
        {studentData ? (
          <div id="pdf-preview-content" style={{
            width: "794px",
            minHeight: "1123px",
            fontFamily: "Arial, sans-serif",
            padding: "15px",
            boxSizing: "border-box",
          }}>
            <ReportHeader />
            <StudentInfoSection data={studentData.student_info} />
            <ParentInfoSection parentInfo={studentData.parent_info} />
            <AddressSection studentInfo={studentData.student_info} />
            <StudentMiscReceipts
              variant="pdf"
              studentId={studentData.student_info?.student_id || studentData.student_info?.studentid}
            />
          </div>
        ) : (
          <p>Loading student data...</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={generatePDF} disabled={isDownloading}>
          {isDownloading ? "Generating PDF..." : "Download PDF"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default Student360Report;